export type SkinTranslucencySetting = "opaque" | "translucent" | "outlines" | "none";

export const DEFAULT_SKIN_TRANSLUCENCY: SkinTranslucencySetting = "opaque";
export const LEGACY_TRANSLUCENT_SKIN_ALPHA = 0.85;

type SkinMeshIndices = number[] | Uint16Array | Uint32Array;

const originalSkinMeshIndices = new WeakMap<SkinRuntimeMesh, SkinMeshIndices>();

export interface SkinRuntimeMesh {
  isVisible?: boolean;
  visibility?: number;
  renderingGroupId?: number;
  isPickable?: boolean;
  mustDepthSortFacets?: boolean;
  facetDepthSortFrom?: unknown;
  material?: {
    alpha?: number;
    backFaceCulling?: boolean;
    transparencyMode?: unknown;
  } | null;
  setEnabled?(value: boolean): void;
  getIndices?(): ArrayLike<number> | null;
  updateFacetData?(): void;
  updateIndices?(indices: SkinMeshIndices, offset?: number, gpuMemoryOnly?: boolean): void;
}

function cloneIndices(indices: ArrayLike<number>): SkinMeshIndices {
  if (indices instanceof Uint16Array) {
    return new Uint16Array(indices);
  }

  if (indices instanceof Uint32Array) {
    return new Uint32Array(indices);
  }

  return Array.from(indices);
}

export function rememberSkinMeshGeometry(mesh: SkinRuntimeMesh): void {
  const indices = mesh.getIndices?.();

  if (!indices || originalSkinMeshIndices.has(mesh)) {
    return;
  }

  originalSkinMeshIndices.set(mesh, cloneIndices(indices));
}

function restoreSkinMeshGeometry(mesh: SkinRuntimeMesh): void {
  const indices = originalSkinMeshIndices.get(mesh);

  if (!indices || !mesh.updateIndices) {
    return;
  }

  mesh.updateIndices(cloneIndices(indices));
}

// Potential optimization: update only when camera position changes beyond an epsilon.
export function updateSkinFacetDepthSorting({
  cameraPosition,
  skinMeshes,
}: {
  cameraPosition: unknown;
  skinMeshes: SkinRuntimeMesh[];
}): void {
  skinMeshes.forEach((mesh) => {
    if (mesh.isVisible === false) {
      return;
    }

    mesh.mustDepthSortFacets = true;
    mesh.facetDepthSortFrom = cameraPosition;
    mesh.updateFacetData?.();
  });
}

export function applySkinTranslucencySetting({
  BABYLON,
  skinMeshes,
  setting,
}: {
  BABYLON: Window["BABYLON"];
  skinMeshes: SkinRuntimeMesh[];
  setting: SkinTranslucencySetting;
}): void {
  const hasSkin = setting !== "none";
  const isOpaque = setting === "opaque";
  const isTranslucent = setting === "translucent";
  const isOutlined = setting === "outlines";
  const skinOpacity = isTranslucent ? LEGACY_TRANSLUCENT_SKIN_ALPHA : 1;
  const opaqueTransparencyMode = BABYLON.PBRMetallicRoughnessMaterial?.PBRMATERIAL_OPAQUE ?? 0;

  skinMeshes.forEach((mesh) => {
    mesh.setEnabled?.(hasSkin);
    mesh.isVisible = hasSkin;
    mesh.visibility = skinOpacity;
    mesh.renderingGroupId = isOutlined ? 0 : 1;
    mesh.isPickable = isOpaque && hasSkin;
    mesh.mustDepthSortFacets = isTranslucent;

    if (!isTranslucent) {
      restoreSkinMeshGeometry(mesh);
    }

    const material = mesh.material;

    if (!material) {
      return;
    }

    material.alpha = 1;
    material.backFaceCulling = isTranslucent;
    material.transparencyMode = isOpaque ? opaqueTransparencyMode : null;
  });
}
